// A list read a page at a time with a cursor (equipment.list, stock.catalog): the first page for the
// input as it is now, then each next page added on. A new input starts again from its first page, and
// a page that comes back for an input no longer shown is dropped.
import { useCallback, useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';

export interface Paged<R, A> {
  rows: R[] | null;
  /** The first page's whole answer (equipment.list's kinds come with it). */
  first: A | null;
  error: string | null;
  /** The next page, when there is one and none is being read. */
  more: (() => void) | null;
  /** A next page is being read. */
  busy: boolean;
  reload: () => void;
}

/** The pages read so far as one list: a later page adds the rows not already in it. */
export function addRows<R extends { id: string }>(had: R[], page: R[]): R[] {
  const seen = new Set(had.map((r) => r.id));
  return [...had, ...page.filter((r) => !seen.has(r.id))];
}

/** A list's next page added on: asked for after the page before's `next`, its new rows under the rest. */
export async function readOn<R extends { id: string }, A extends { next: string | null }>(
  call: (action: string, input: Record<string, unknown>) => Promise<A>,
  action: string,
  input: Record<string, unknown>,
  had: { rows: R[]; next: string },
  rowsOf: (a: A) => R[],
): Promise<{ rows: R[]; next: string | null }> {
  const a = await call(action, { ...input, after: had.next });
  return { rows: addRows(had.rows, rowsOf(a)), next: a.next };
}

export function usePaged<R extends { id: string }, A extends { next: string | null }>(
  action: string,
  input: Record<string, unknown> | null,
  rowsOf: (a: A) => R[],
): Paged<R, A> {
  const key = JSON.stringify(input);
  const [state, setState] = useState<{ key: string; rows: R[]; first: A; next: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const shown = useRef(key);
  shown.current = key;
  useEffect(() => {
    if (!input) return;
    let current = true;
    setError(null);
    client
      .call<A>(action, input)
      .then((a) => current && setState({ key, rows: rowsOf(a), first: a, next: a.next }))
      .catch((e: unknown) => current && setError(errorText(e, 'Could not load this.')));
    return () => {
      current = false;
    };
    // the input is compared by value (key)
  }, [action, key, tick]);
  const here = state?.key === key ? state : null;
  const more = useCallback(() => {
    if (!here?.next || !input) return;
    const at = key;
    setBusy(true);
    readOn((n, i) => client.call<A>(n, i), action, input, { rows: here.rows, next: here.next }, rowsOf)
      .then((p) => {
        if (shown.current !== at) return;
        setState((s) => (s && s.key === at ? { ...s, rows: addRows(s.rows, p.rows), next: p.next } : s));
      })
      .catch((e: unknown) => shown.current === at && setError(errorText(e, 'Could not load the rest.')))
      .finally(() => setBusy(false));
  }, [action, key, here?.next]);
  return {
    rows: here?.rows ?? null,
    first: here?.first ?? null,
    error,
    more: here?.next && !busy ? more : null,
    busy,
    reload: () => setTick((t) => t + 1),
  };
}
